import type { IdentityV1 } from '@alpod-ai-writer/backend';
import type { BillingResidency } from '@alpod-ai-writer/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Button, Label, TextInput } from 'flowbite-react';
import { type FC,useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useApi } from '../../../api/useApi.hook';
import { ErrorSwitch } from '../../../components/errorMessage/errorSwitch.component';
import { Loading } from '../../../components/loading/loading.component';
import { ToastNotificationType } from '../toastNotification/toastNotifications.context';
import { useToastNotifications } from '../toastNotification/useToastNotifications.hook';

interface FormValues {
    billingResidency: BillingResidency|'';
    billingName: string;
    billingStreet: string;
    billingStreetNumber: string;
    billingCity: string;
    billingPostcode: string;
}

export interface BillingFormProps {
    submitLabel?: string;
    showSuccessToast?: boolean;
    onSave?: () => void;
}

export const BillingForm: FC<BillingFormProps> = ({
    submitLabel = 'Uložit fakturační údaje',
    showSuccessToast = true,
    onSave
}) => {
    const api = useApi();
    const queryClient = useQueryClient();
    const toastNotifications = useToastNotifications();

    const { data: identity, error, isLoading } = useQuery({
        queryKey: ['my-identity'],
        queryFn: async () => {
            return api.v1.identities.getMyIdentity();
        }
    });

    const saveUserDetails = useMutation({
        mutationFn: (update: IdentityV1.OwnUpdate) => {
            return api.v1.identities.updateMyIdentity(update);
        },
        onSuccess: () => {
            if (showSuccessToast) {
                toastNotifications.showToast({ type: ToastNotificationType.SUCCESS, message: 'Fakturační údaje byly uloženy' });
            }

            queryClient.invalidateQueries({ queryKey: ['my-identity'] });

            onSave?.();
        }
    });

    const { register, handleSubmit, formState: { errors }, control } = useForm<FormValues>({
        defaultValues: {
            billingResidency: identity?.userFields['billingResidency'] as BillingResidency ?? '',
            billingName: identity?.userFields['billingName'] as string ?? identity?.name ?? '',
            billingStreet: identity?.userFields['billingStreet'] as string ?? '',
            billingStreetNumber: identity?.userFields['billingStreetNumber'] as string ?? '',
            billingCity: identity?.userFields['billingCity'] as string ?? '',
            billingPostcode: identity?.userFields['billingPostcode'] as string ?? ''
        }
    });

    const onSubmit = useCallback((formValues: FormValues) => {
        if (isLoading || !identity) {
            return;
        }

        const update = {
            ...identity,
            userFields: {
                ...identity.userFields,
                ...formValues
            }
        };

        saveUserDetails.mutate(update);
    }, [ identity, isLoading ]);

    return (
        <>
            {!identity && isLoading && (
                <div className='flex flex-row items-center justify-center min-h-screen'>
                    <Loading label='Načítání dat...' />
                </div>
            )}

            <ErrorSwitch
                error={error}
                default={{ message: 'Při načítání dat došlo k chybě. Zkuste to prosím za chvíli znovu.', showDetails: true }}
                options={[]}
                retry={() => queryClient.invalidateQueries({ queryKey: ['my-identity'] })}
            />

            {identity && (
                <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-6'>
                    <div className="flex flex-col items-start justify-start">
                        <Label className="block text-sm font-medium text-gray-700 mb-2">Vyberte místo bydliště</Label>
                        <div className="flex flex-col w-full">
                            <Controller
                                control={control}
                                name="billingResidency"
                                rules={{ required: 'Tohle pole je povinné' }}
                                render={({ field: { onChange, value } }) => (
                                    <div className='flex flex-row justify-start items-center gap-2'>
                                        <ResidencyButton
                                            flag='🇨🇿'
                                            label='Česká republika'
                                            isActive={value === 'CZ'}
                                            onClick={() => onChange('CZ')}
                                        />

                                        <ResidencyButton
                                            flag='🇸🇰'
                                            label='Slovenská republika'
                                            isActive={value === 'SK'}
                                            onClick={() => onChange('SK')}
                                        />
                                    </div>
                                )}
                            />
                            {errors.billingResidency && (
                                <span className="block text-sm text-red-500 mt-1">
                                    {errors.billingResidency.message}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className='flex flex-col gap-4'>
                        <div>
                            <Label htmlFor="billingName" className="block text-sm font-medium text-gray-700">Jméno a příjmení</Label>
                            <TextInput
                                {...register('billingName', { required: 'Tohle pole je povinné' })}
                                color={errors.billingName ? 'failure' : 'default'}
                                className='w-[20rem] max-w-full'
                            />
                            {errors.billingName && (
                                <span className="block text-sm text-red-500 mt-1">
                                    {errors.billingName.message}
                                </span>
                            )}
                        </div>

                        <div className='flex flex-col md:flex-row gap-6 w-full'>
                            <div className='flex-grow'>
                                <Label htmlFor="billingStreet" className="block text-sm font-medium text-gray-700">Ulice</Label>
                                <TextInput
                                    {...register('billingStreet', { required: 'Tohle pole je povinné' })}
                                    color={errors.billingStreet ? 'failure' : 'default'}
                                />
                                {errors.billingStreet && (
                                    <span className="block text-sm text-red-500 mt-1">
                                        {errors.billingStreet.message}
                                    </span>
                                )}
                            </div>

                            <div>
                                <Label htmlFor="billingStreetNumber" className="block text-sm font-medium text-gray-700">Č. p. / orientační</Label>
                                <TextInput
                                    {...register('billingStreetNumber', { required: 'Tohle pole je povinné' })}
                                    color={errors.billingStreetNumber ? 'failure' : 'default'}
                                    size={14}
                                />
                                {errors.billingStreet && (
                                    <span className="block text-sm text-red-500 mt-1">
                                        {errors.billingStreet.message}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className='flex flex-col md:flex-row gap-6'>
                            <div>
                                <Label htmlFor="billingCity" className="block text-sm font-medium text-gray-700">Město</Label>
                                <TextInput
                                    {...register('billingCity', { required: 'Tohle pole je povinné' })}
                                    color={errors.billingCity ? 'failure' : 'default'}
                                    className='w-[20rem] max-w-full'
                                />
                                {errors.billingCity && (
                                    <span className="block text-sm text-red-500 mt-1">
                                        {errors.billingCity.message}
                                    </span>
                                )}
                            </div>
                            <div>
                                <Label htmlFor="billingPostcode" className="block text-sm font-medium text-gray-700">PSČ</Label>
                                <TextInput
                                    {...register('billingPostcode', {
                                        required: 'Tohle pole je povinné',
                                        pattern: {
                                            message: 'Zadejte prosím platné poštovní číslo',
                                            value: /^\d{3}\s?\d{2}$/
                                        }
                                    })}
                                    color={errors.billingPostcode ? 'failure' : 'default'}
                                    className='w-32 max-w-full'
                                />
                                {errors.billingPostcode && (
                                    <span className="block text-sm text-red-500 mt-1">
                                        {errors.billingPostcode.message}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className='flex flex-row items-center justify-start gap-2'>
                        <Button
                            type='submit'
                            size='lg'
                            color='primary'
                            disabled={saveUserDetails.isPending}
                        >{submitLabel}</Button>
                    </div>

                    <ErrorSwitch
                        error={saveUserDetails.error}
                        default={{ message: 'Při ukládání dat došlo k chybě. Zkuste to prosím za chvíli znovu.', showDetails: true }}
                        options={[]}
                    />
                </form>
            )}
        </>
    );
};

const ResidencyButton: FC<{
    flag: string,
    label: string,
    isActive: boolean,
    onClick: () => void
}> = ({
    flag,
    label,
    isActive,
    onClick
}) => {
    return (
        <button
            type='button'
            onClick={onClick}
            className={clsx(
                'flex flex-row items-center gap-2 py-2 px-4 rounded-md cursor-pointer transition-colors duration-200',
                'border',
                !isActive && 'bg-transparent text-primary-500 border-gray-300 hover:border-primary-500',
                isActive && 'bg-primary-500 text-white border-primary-500'
            )}
        >
            <span className='flex text-3xl leading-none'>{flag}</span>
            <span className='flex text-base leading-none justify-center font-medium'>{label}</span>
        </button>
    );
};
